// Pure model of a person's access (no client imports, so it's unit-testable): mirrors app.my_role /
// app.can in db/neon/2026-10-10-roles-phase1.sql. The database stays the security layer.
const RANK = { admin: 4, owner: 3, manager: 2, crew: 1 };
const byRank = (a, b) => (RANK[b] || 0) - (RANK[a] || 0);

export function computeAccess(rows = []) {
  const roleFor = (propertyId) => {
    const roles = rows
      .filter(r => r.property_id === null || (propertyId && r.property_id === propertyId))
      .map(r => r.role)
      .sort(byRank);
    return roles[0] || null;
  };
  const topRole = rows.map(r => r.role).sort(byRank)[0] || null;
  const isAdmin = rows.some(r => r.role === 'admin' && r.property_id === null);

  const can = (propertyId, action) => {
    const role = roleFor(propertyId);
    if (!role) return false;
    switch (action) {
      case 'read': return true;
      case 'edit':
      case 'proposal_costs': return ['admin', 'owner', 'manager'].includes(role);
      case 'financials':
      case 'admin':
      case 'manage_people': return ['admin', 'owner'].includes(role);
      case 'secrets':
        return ['admin', 'owner', 'manager'].includes(role)
          || rows.some(r => r.role === 'crew' && r.can_see_access_info
                            && (r.property_id === null || r.property_id === propertyId));
      default: return false;
    }
  };
  // "Anywhere" checks for data that isn't tied to one property (nav, vendors).
  const canAnywhere = (action) => rows.some(r => can(r.property_id, action));

  const hasAdminSection = rows.some(r => r.role === 'admin' || r.role === 'owner');
  const isCrewOnly = topRole === 'crew';

  // Views the nav offers and App lets through. The database still filters every row.
  const views = new Set();
  if (topRole && !isCrewOnly) {
    ['tasks', 'jobs', 'templates', 'vendors', 'property-hub'].forEach(v => views.add(v));
    if (canAnywhere('financials')) { views.add('calendar'); views.add('revenue'); }
    if (hasAdminSection) views.add('admin');
  }
  if (isCrewOnly) views.add('my-work');

  return {
    rows,
    email: rows[0]?.email || null,
    topRole,
    isAdmin,
    isCrewOnly,
    hasAdminSection,
    hasAccess: rows.length > 0,
    roleFor,
    can,
    canAnywhere,
    canEditVendors: topRole === 'admin' || topRole === 'owner',
    views,
    homeView: isCrewOnly ? 'my-work' : 'tasks',
  };
}
